// @flow

import React, { type Node, useMemo } from 'react';
import { getSafeCountryFromCode, CountryProvider } from '@tectonics/commons/country';
import { getTenantFromCode, TenantProvider } from '@tectonics/commons/tenant';
import { getQueryParamValue } from '@tectonics/commons/query-params';
import { VolcanesThemeProvider, getThemeById } from '@tectonics/look-and-feel/volcanes/theme';
import { GlobalStyle } from '@tectonics/look-and-feel/volcanes/commons/global-style';
import { EnrollmentProvider, useEnrollment } from './context';
import { useEnrollmentState } from './hooks/use-enrollment-state';
import { BankLinkingScreen } from './screens/bank-linking-screen';
import { BankRedirectScreen } from './screens/bank-redirect-screen';
import { PollingAuthScreen } from './screens/polling-auth-screen';
import { SuccessScreen } from './screens/success-screen';
import { ErrorScreen } from './screens/error-screen';
import { PageContainer } from './controller.styles';

type Props = {|
  +tenantCode: ?string,
|};

export function PixJsrEnrollmentController({ tenantCode }: Props): Node {
  if (!tenantCode) {
    throw new Error('URL query param "tenant" not found');
  }

  const contract = getQueryParamValue('contract');
  if (!contract) {
    throw new Error('URL query param "contract" not found');
  }

  const tenant = getTenantFromCode(tenantCode);
  const country = getSafeCountryFromCode('br');
  const theme = getThemeById('default');

  const step = getQueryParamValue('step');

  return (
    <TenantProvider value={tenant}>
      <CountryProvider value={country}>
        <VolcanesThemeProvider theme={theme}>
          <>
            <GlobalStyle />
            <EnrollmentRoot contract={contract} step={step} />
          </>
        </VolcanesThemeProvider>
      </CountryProvider>
    </TenantProvider>
  );
}

type EnrollmentRootProps = {|
  +contract: string,
  +step: ?string,
|};

function EnrollmentRoot({ contract, step }: EnrollmentRootProps): Node {
  const [data, dispatch] = useEnrollmentState(contract, step);

  const contextValue = useMemo(() => ({ data, dispatch }), [data, dispatch]);

  return (
    <EnrollmentProvider value={contextValue}>
      <PageContainer>
        <EnrollmentScreen />
      </PageContainer>
    </EnrollmentProvider>
  );
}

function EnrollmentScreen(): Node {
  const { data } = useEnrollment();

  switch (data.state) {
    case 'BANK_LINKING':
      return <BankLinkingScreen />;
    case 'INITIATING':
    case 'BANK_REDIRECT':
      return <BankRedirectScreen />;
    case 'POLLING':
    case 'READY_TO_AUTH':
    case 'AUTHENTICATING':
    case 'SUBMITTING_AUTH':
      return <PollingAuthScreen />;
    case 'SUCCESS':
      return <SuccessScreen />;
    case 'ERROR':
      return <ErrorScreen />;
    default:
      return <BankLinkingScreen />;
  }
}
